import { useSurface, type SurfaceKey } from "@/contexts/surface-context";
import {
  captureEngagement as engineCaptureEngagement,
  resolvePosition,
  statusTrackingTarget,
} from "@/impressions";

import type { EngagementAction, Status } from "@/schemas";

/**
 * Plain, serializable snapshot of a status's engagement-relevant fields plus the ambient surface
 * they were captured under, taken at the moment a reply/quote is initiated (e.g. tapping Reply)
 * rather than at composer submit — the composer has no ambient surface of its own, so the
 * originating feed context must be carried through composer state to survive to submit success
 */
interface EngagementSnapshot {
  statusId: string;
  authorAccountId?: string;
  signature?: string;
  surface: SurfaceKey;
  position?: number;
}

/**
 * Emit engagement events for committed user actions on a status, tagged with the
 * ambient surface from the nearest <SurfaceProvider> (and its served-list position, when there is
 * one). No ambient surface — an uninstrumented screen — means nothing is captured, the same
 * convention impressions use.
 */
const useEngagementCapture = () => {
  const ambient = useSurface();

  const captureEngagement = (
    action: EngagementAction,
    status: Status,
    targetAccountId?: string,
  ): void => {
    if (!ambient?.surface) return;
    const target = statusTrackingTarget(status);
    if (!target) return;

    engineCaptureEngagement({
      action,
      target,
      surface: ambient.surface,
      position: resolvePosition(ambient.positionQueryKey, target.itemId),
      targetAccountId,
    });
  };

  const snapshotEngagementContext = (
    status: Status,
  ): EngagementSnapshot | undefined => {
    if (!ambient?.surface) return undefined;
    const target = statusTrackingTarget(status);
    if (!target) return undefined;

    const position = resolvePosition(ambient.positionQueryKey, target.itemId);
    return {
      statusId: target.statusId,
      authorAccountId: target.authorAccountId,
      signature: target.signature,
      surface: ambient.surface,
      position: position ?? undefined,
    };
  };

  const captureFromSnapshot = (
    action: EngagementAction,
    snapshot: EngagementSnapshot | undefined,
  ): void => {
    if (!snapshot) return;

    engineCaptureEngagement({
      action,
      target: {
        itemId: snapshot.statusId,
        statusId: snapshot.statusId,
        authorAccountId: snapshot.authorAccountId,
        signature: snapshot.signature,
        isReblog: false,
        isQuote: false,
        isVideo: false,
      },
      surface: snapshot.surface,
      position: snapshot.position ?? null,
    });
  };

  return { captureEngagement, snapshotEngagementContext, captureFromSnapshot };
};

export { useEngagementCapture, type EngagementSnapshot };
